import React, { useMemo } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import {loadStripe} from "@stripe/stripe-js"
import { useAuth } from '../context/AuthContext'
import { useVendorAuth } from '../context/VendorAuthContext'
import { useCart, availableProducts } from '../context/CartContext'

const CartSection = () => {
    const { user, token, isAuthenticated } = useAuth();
    const { isVendorAuthenticated } = useVendorAuth();
    const { cartItems, addToCart, removeFromCart } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const returnPath = location.state?.from ?? "";

    // -----------------------------
    // TOTALS
    // -----------------------------
    const subtotal = useMemo(() => {
        return cartItems.reduce((acc, item) => acc + item.price, 0)
    }, [cartItems])

    const total = subtotal.toFixed(2)

    // -----------------------------
    // STRIPE PAYMENT
    // -----------------------------
   const makePayment = async () => {
        if (!isAuthenticated) {
            navigate("/account-login");
            return;
        }

        try {

            const stripe = await loadStripe(
                process.env.REACT_APP_STRIPE_API_KEY
            );

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/create-checkout-session`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        ...(token && { "Authorization": `Bearer ${token}` }),
                    },
                    body: JSON.stringify({
                        items: cartItems,
                        user_id: user?.id ?? null,
                        return_path: returnPath,
                    }),
                }
            );

            const session = await response.json();

            if (session.checkout_url) {
                window.location.href = session.checkout_url;
            }

            if (response.error) {
                console.log(response.error);
            }

        } catch (error) {
            console.log("Payment error:", error);
        }
    };

    if (isVendorAuthenticated) {
        return (
            <section className="cart py-80">
                <div className="container container-lg">
                    <div className="row justify-content-center">
                        <div className="col-lg-6 text-center">
                            <div className="border border-gray-100 rounded-16 p-48">
                                <span className="d-flex justify-content-center mb-24" style={{ fontSize: 64, color: '#94a3b8' }}>
                                    <i className="ph-fill ph-storefront" />
                                </span>
                                <h4 className="mb-16">Vendor Accounts Cannot Purchase a Fun Card</h4>
                                <p className="text-gray-600 mb-32">
                                    The Fun Card membership is available to individual customers only. To purchase, please log in with a personal user account.
                                </p>
                                <Link
                                    to="/vendor-dashboard"
                                    className="btn rounded-8 px-32 py-14"
                                    style={{ backgroundColor: '#4F7DC0', borderColor: '#4F7DC0', color: '#fff' }}
                                >
                                    Back to Vendor Dashboard
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="cart py-80">
            <div className="container container-lg">

                <div className="row gy-4">

                    {/* ========================= */}
                    {/* CART TABLE */}
                    {/* ========================= */}
                    <div className="col-xl-9 col-lg-8">

                        <div className="cart-table border border-gray-100 rounded-8 px-40 py-48">

                            <div className="overflow-x-auto scroll-sm scroll-sm-horizontal">

                                <table className="table style-three">

                                    <thead>
                                        <tr>
                                            <th className="h6 mb-0 text-lg fw-bold">
                                                Product Name
                                            </th>

                                            <th className="h6 mb-0 text-lg fw-bold">
                                                Price
                                            </th>

                                            <th className="h6 mb-0 text-lg fw-bold">
                                                Action
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>

                                        {cartItems.map((item) => (

                                            <tr key={item.id}>

                                                {/* PRODUCT */}
                                                <td>

                                                    <div className="table-product d-flex align-items-center gap-24">

                                                        <Link
                                                            to="/product-details-two"
                                                            className="table-product__media border border-gray-100 rounded-8 overflow-hidden d-block d-flex align-items-center justify-content-center"
                                                        >
                                                            <img
                                                                src={item.image}
                                                                className="w-90 h-100 object-fit-cover"
                                                                alt=""
                                                            />
                                                        </Link>

                                                        <div className="table-product__content text-start">

                                                            <h6 className="title text-lg fw-semibold mb-8">

                                                                <Link
                                                                    to="/product-details"
                                                                    className="link text-line-2"
                                                                >
                                                                    {item.title}
                                                                </Link>

                                                            </h6>

                                                            <div className="flex-align gap-16 mb-16">

                                                                <div className="flex-align gap-6">

                                                                    <span className="text-md fw-medium text-warning-600 d-flex">
                                                                        <i className="ph-fill ph-star" />
                                                                    </span>

                                                                    <span className="text-md fw-semibold text-gray-900">
                                                                        {item.rating}
                                                                    </span>

                                                                </div>

                                                                <span className="text-sm fw-medium text-gray-200">
                                                                    |
                                                                </span>

                                                                <span className="text-neutral-600 text-sm">
                                                                    {item.reviews} Reviews
                                                                </span>

                                                            </div>

                                                            {item.tags.length > 0 && (
                                                                <div className="flex-align gap-16">
                                                                    {item.tags.map((tag, index) => (
                                                                        <span
                                                                            key={index}
                                                                            style={{
                                                                                display: 'inline-block',
                                                                                background: '#f1f5f9',
                                                                                color: '#475569',
                                                                                fontSize: 12,
                                                                                fontWeight: 600,
                                                                                padding: '4px 10px',
                                                                                borderRadius: 99,
                                                                                border: '1px solid #e2e8f0',
                                                                                cursor: 'default',
                                                                                userSelect: 'none',
                                                                            }}
                                                                        >
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}

                                                        </div>

                                                    </div>

                                                </td>

                                                {/* PRICE */}
                                                <td>
                                                    <span className="text-lg h6 mb-0 fw-semibold">
                                                        ${item.price.toFixed(2)}
                                                    </span>
                                                </td>

                                                {/* REMOVE */}
                                                <td>

                                                    {item.id !== 0 && (
                                                        <button
                                                            onClick={() => removeFromCart(item.id)}
                                                            className="btn btn-danger px-16 py-8 rounded-8"
                                                        >
                                                            Remove
                                                        </button>
                                                    )}

                                                </td>

                                            </tr>

                                        ))}

                                    </tbody>

                                </table>

                            </div>

                        </div>

                        {/* ========================= */}
                        {/* SLIDING ADD-ON SECTION */}
                        {/* ========================= */}
                        <div className="border border-gray-100 rounded-8 p-32 mt-32 overflow-hidden">

                            <div className="flex-between mb-24">
                                <h5 className="mb-0">
                                    Recommended Add-ons
                                </h5>

                                <div className="flex-align gap-12" style={{ display: 'none' }}>
                                    <button
                                        className="btn bg-main-600 text-white hover-bg-main-700 rounded-circle"
                                        type="button"
                                        data-bs-target="#addonCarousel"
                                        data-bs-slide="prev"
                                    >
                                        <i className="ph ph-caret-left" />
                                    </button>

                                    <button
                                        className="btn bg-main-600 text-white hover-bg-main-700 rounded-circle"
                                        type="button"
                                        data-bs-target="#addonCarousel"
                                        data-bs-slide="next"
                                    >
                                        <i className="ph ph-caret-right" />
                                    </button>
                                </div>
                            </div>

                            {/* BOOTSTRAP CAROUSEL */}
                            <div
                                id="addonCarousel"
                                className="carousel slide"
                                data-bs-ride="false"
                            >

                                <div className="carousel-inner">

                                    {availableProducts.map((product, index) => {

                                        const exists = cartItems.find(
                                            item => item.id === product.id
                                        )

                                        return (
                                            <div
                                                key={product.id}
                                                className={`carousel-item ${index === 0 ? "active" : ""}`}
                                            >

                                                <div className="table-product d-flex align-items-center justify-content-between gap-24 border border-gray-100 rounded-12 p-24">

                                                    <div className="d-flex align-items-center gap-24">

                                                        <div className="table-product__media border border-gray-100 rounded-8 overflow-hidden d-flex align-items-center justify-content-center">
                                                            <img
                                                                src={product.image}
                                                                className="w-90 h-100 object-fit-cover"
                                                                alt=""
                                                            />
                                                        </div>

                                                        <div>

                                                            <h6 className="fw-semibold mb-8">
                                                                {product.title}
                                                            </h6>

                                                            <div className="mb-12">
                                                                <span className="text-warning-600">
                                                                    ★
                                                                </span>{" "}
                                                                {product.rating}
                                                            </div>

                                                            {product.tags.length > 0 && (
                                                                <div className="flex-align gap-8 flex-wrap">
                                                                    {product.tags.map((tag, index) => (
                                                                        <span
                                                                            key={index}
                                                                            className="bg-gray-100 px-12 py-4 rounded-pill text-sm"
                                                                        >
                                                                            {tag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}

                                                        </div>

                                                    </div>

                                                    <div className="text-end">

                                                        <h5 className="mb-16">
                                                            ${product.price.toFixed(2)}
                                                        </h5>

                                                        {!exists ? (
                                                            <button
                                                                onClick={() => addToCart(product)}
                                                                className="btn bg-main-600 text-white hover-bg-main-700 rounded-8 px-24 py-12"
                                                            >
                                                                Add To Cart
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => removeFromCart(product.id)}
                                                                className="btn btn-danger rounded-8 px-24 py-12"
                                                            >
                                                                Remove
                                                            </button>
                                                        )}

                                                    </div>

                                                </div>

                                            </div>
                                        )
                                    })}

                                </div>

                            </div>

                        </div>

                    </div>

                    {/* ========================= */}
                    {/* CART TOTALS */}
                    {/* ========================= */}
                    <div className="col-xl-3 col-lg-4">

                        <div className="cart-sidebar border border-gray-100 rounded-8 px-24 py-40">

                            <h6 className="text-xl mb-32">
                                Cart Totals
                            </h6>

                            <div className="bg-color-three rounded-8 p-24 mt-24">

                                <div className="flex-between gap-8">

                                    <span className="text-gray-900 text-xl fw-semibold">
                                        Total
                                    </span>

                                    <span className="text-gray-900 text-xl fw-semibold">
                                        ${total}
                                    </span>

                                </div>

                            </div>

                            <button
                                onClick={makePayment}
                                className="btn bg-main-600 text-white hover-bg-main-700 mt-40 py-18 w-100 rounded-8"
                            >
                                Proceed to checkout
                            </button>

                        </div>

                    </div>

                </div>

            </div>
        </section>
    )
}

export default CartSection